import { createContext, useContext, useState, useEffect } from "react";
import api from "../lib/api";
import { forgetDisclaimerAcknowledgements } from "../lib/disclaimerGate";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (error) {
          console.error("Auth init error:", error);
          localStorage.removeItem("token");
          forgetDisclaimerAcknowledgements();
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    // A flag belongs to one session. The token lives in localStorage and is
    // shared across tabs, the flags are per tab, so a logout in ANOTHER tab
    // leaves this one's behind; clearing at session start closes that.
    forgetDisclaimerAcknowledgements();
    
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (data) => {
    const response = await api.post("/auth/register", data);
    const { access_token, user: userData } = response.data;
    forgetDisclaimerAcknowledgements();
    
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    // STARSEED-CAPMARKETS-257 — the disclaimer gate's per-member session
    // flags belong to the session that is ending (also on a 401, in
    // lib/api.js, and on a failed bootstrap above).
    forgetDisclaimerAcknowledgements();
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await api.get("/auth/me");
        setUser(response.data);
      } catch (error) {
        console.error("Refresh user error:", error);
      }
    }
  };

  // Used by /register/success after Stripe Checkout completes — hydrates
  // auth context from a backend-issued token without forcing a reload.
  const setUserFromToken = async (access_token, userData) => {
    localStorage.setItem("token", access_token);
    setToken(access_token);
    if (userData) setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      refreshUser,
      setUserFromToken,
      isAdmin: user?.role === "admin" || user?.role === "superuser",
      isReferrer: user?.role === "referrer" || user?.role === "admin" || user?.role === "superuser"
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
